import { Box, Stack, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { UserInvitationExtendedDto } from 'tdc-web-backend/user-invitation/schemas';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Link from '../../../../components/link/Link';
import BaseInputLabel from '../../../../components/custom-inputs/BaseInputLabel';
import CustomInput from '../../../../components/custom-input/CustomInput';
import useAuth from '../../../../utils/hooks/useAuth';
import CustomButton from '../../../../components/button/CustomButton';
import SignInLink from './SignInLink';
import InvalidToken from './InvalidToken';

interface AcceptFormProps {
  invitationData: UserInvitationExtendedDto;
  token: string;
}

const AcceptForm = ({ invitationData, token }: AcceptFormProps) => {
  const [showPass, setShowPass] = useState<Record<string, boolean>>({
    password: false,
    repeatPassword: false,
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const { acceptInvitation, isLoadingAcceptInvitation } = useAuth();
  const { t } = useTranslation();
  const formMethods = useForm({
    defaultValues: {
      jobTitle: invitationData.jobTitle || '',
      firstName: invitationData.firstName || '',
      lastName: invitationData.lastName || '',
      confirmPassword: '',
      password: '',
      TermsAndConditions: false,
    },
    mode: 'onChange',
  });
  const { watch, handleSubmit, getValues } = formMethods;

  const onSubmit = () => {
    acceptInvitation(
      {
        token: token as string,
        jobTitle: getValues('jobTitle'),
        password: getValues('password'),
        firstName: getValues('firstName'),
        lastName: getValues('lastName'),
      },
      {
        onError: (error) => {
          setErrorMsg(error.error as string);
        },
      },
    );
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {errorMsg ? (
          <InvalidToken message={errorMsg} />
        ) : (
          <Stack spacing={3}>
            <Box
              sx={{
                // border: '1px solid',
                borderColor: 'primaryDark.400',
                borderRadius: '4px',
                backgroundColor: 'primaryLight.50',
                p: 1,
              }}
            >
              <Typography
                variant="body3"
                sx={{
                  lineHeight: '1.25rem',
                  color: 'primaryDark.600',
                }}
              >
                {t('register.acceptInvitation.text1')}
                <Link
                  sx={{ fontFamily: 'Gellix-SemiBold' }}
                  to={`mailto:${invitationData.sentBy.email}`}
                >
                   {invitationData.sentBy.email}
                </Link>{' '}
                {t('register.acceptInvitation.text2')}
              </Typography>
            </Box>

            <Stack spacing={0.25}>
              <BaseInputLabel text={t('common.email')} sx={{ mb: 0 }} />

              <Typography variant="bodyMedium2" color="primaryDark.800">
                {invitationData.email}
              </Typography>
            </Stack>

            <CustomInput
              input={<CustomInput.Text />}
              name="firstName"
              label={t('common.firstName')}
              disabled={isLoadingAcceptInvitation}
              required
            />

            <CustomInput
              input={<CustomInput.Text />}
              name="lastName"
              label={t('common.lastName')}
              disabled={isLoadingAcceptInvitation}
              required
            />

            <CustomInput
              input={<CustomInput.Text />}
              name="jobTitle"
              label={t('common.jobTitle')}
              disabled={isLoadingAcceptInvitation}
              required
            />

            <CustomInput
              input={<CustomInput.Text />}
              type={showPass.password ? 'text' : 'password'}
              name="password"
              label={t('security.password')}
              disabled={isLoadingAcceptInvitation}
              required
              defaultHelperText={t('security.passwordRequirements')}
              placeholder="••••••••"
              adornment={{
                icon: showPass.password ? (
                  <VisibilityIcon sx={{ color: 'primaryDark.400', width: 22, height: 22 }} />
                ) : (
                  <VisibilityOffIcon sx={{ color: 'primaryDark.400', width: 22, height: 22 }} />
                ),
                onClick: () => {
                  setShowPass({ ...showPass, password: !showPass.password });
                },
              }}
              validationRegEx={{
                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\-]).{8,}$/,
                message: t('security.passwordRequirements'),
              }}
              rules={{
                maxLength: {
                  value: 127,
                  message: t('security.passwordTooLong'),
                },
              }}
            />

            <CustomInput
              input={<CustomInput.Text />}
              type={showPass.repeatPassword ? 'text' : 'password'}
              name="confirmPassword"
              label={t('security.repeatPassword')}
              disabled={isLoadingAcceptInvitation}
              required
              placeholder="••••••••"
              adornment={{
                icon: showPass.repeatPassword ? (
                  <VisibilityIcon sx={{ color: 'primaryDark.400', width: 22, height: 22 }} />
                ) : (
                  <VisibilityOffIcon sx={{ color: 'primaryDark.400', width: 22, height: 22 }} />
                ),
                onClick: () => {
                  setShowPass({ ...showPass, repeatPassword: !showPass.repeatPassword });
                },
              }}
              validate={() =>
                watch('confirmPassword') === watch('password') || t('security.passwordsDoNotMatch')
              }
              rules={{
                maxLength: {
                  value: 127,
                  message: t('security.passwordTooLong'),
                },
              }}
            />

            <CustomInput
              input={<CustomInput.UserAgreement />}
              name="TermsAndConditions"
              disabled={isLoadingAcceptInvitation}
              required
              customRequiredMessage={t('errors.agreeToTerms')}
            />

            <Stack
              spacing={2}
              direction={{ xs: 'column-reverse', lg: 'row' }}
              alignSelf="center"
              width="100%"
              justifyContent="space-between"
            >
              <SignInLink disabled={isLoadingAcceptInvitation} />
              <CustomButton
                type="submit"
                loading={isLoadingAcceptInvitation}
                variant="primary"
                sx={{ width: { xs: '100%', lg: '55%' } }}
              >
                {t('register.createAccount')}
              </CustomButton>
            </Stack>
          </Stack>
        )}
      </form>
    </FormProvider>
  );
};
export default AcceptForm;
